import { useMemo } from 'react'
import { NoData } from 'src/components/analytics/common/NoData'
import BreakdownTable, { BreakdownItem } from 'src/components/analytics/tables/BreakdownTable'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { formatNumberToTwoDecimals, percentageWithFixedDecmials } from 'src/lib/utils/analytics'
import { ChartSkeleton } from '../common/Skeleton'

const SALES_BREAKDOWN_KEYS = {
  net_sales: 'Net sales',
  orders_placed: 'Orders placed',
  orders_cancelled: 'Cancelled',
  orders_refunded: 'Returned'
}

type SalesBreakdownRawData = {
  dimension: 'net_sales' | 'orders_placed' | 'orders_cancelled' | 'orders_refunded'
  data: {
    x: number
  }[]
}[]

const SalesBreakDownTable = () => {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.orders_sales_breakdown,
      dependencies: {
        endpoints: ['/shopflo-orders-sales']
      }
    },
    refreshKey
  })
  const chartBreakdownData: BreakdownItem[] = useMemo(() => {
    const rawData: SalesBreakdownRawData = data[0]?.data?.result?.widgetData?.data
    const comparedData: SalesBreakdownRawData = data[0]?.data?.result?.widgetData?.compared_data

    const mappedData = rawData?.map(item => {
      return {
        label: SALES_BREAKDOWN_KEYS[item.dimension],
        value: formatNumberToTwoDecimals(item.data[0].x)
      }
    })

    // Ensure 'Net sales' is always the first item
    const sortedMappedData = mappedData?.sort((a, b) => {
      if (a.label === 'Net sales') return -1
      if (b.label === 'Net sales') return 1
      return 0
    })

    const comparedMappedData = comparedData?.map(item => {
      return {
        label: SALES_BREAKDOWN_KEYS[item.dimension],
        value: formatNumberToTwoDecimals(item.data[0].x)
      }
    })

    const sortedComparedMappedData = comparedMappedData?.sort((a, b) => {
      if (a.label === 'Net sales') return -1
      if (b.label === 'Net sales') return 1
      return 0
    })

    if (sortedComparedMappedData && sortedComparedMappedData.length) {
      return sortedMappedData?.map((item, index) => {
        return {
          ...item,
          value: item.value.toLocaleString('en-IN', { maximumFractionDigits: 2, style: 'currency', currency: 'INR' }),
          change: percentageWithFixedDecmials(item.value, sortedComparedMappedData[index]?.value || 0),
          bold: index === 0,
          reverseComparison: ['Returned', 'Cancelled'].includes(item.label)
        }
      })
    }

    return sortedMappedData?.map((item, index) => {
      return {
        label: item.label,
        value: item.value.toLocaleString('en-IN', { maximumFractionDigits: 2, style: 'currency', currency: 'INR' }),
        change: 0,
        bold: index === 0
      }
    })
  }, [data])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (chartBreakdownData && chartBreakdownData.length > 0) {
      return (
        <div className='h-full pt-4'>
          <BreakdownTable data={chartBreakdownData} />
        </div>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}

export default SalesBreakDownTable
